import i18n from '../i18n';

// union_exams.next_test_date / registration_deadline are Postgres `date` columns ("YYYY-MM-DD").
// `new Date("YYYY-MM-DD")` parses as UTC midnight, which renders as the previous day for every
// US timezone -- so these are always built as local dates instead.
export function parseExamDate(value) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatExamDate(value, { weekday = false } = {}) {
  const date = parseExamDate(value);
  if (!date) return null;
  return date.toLocaleDateString(i18n.language, {
    ...(weekday ? { weekday: 'short' } : {}),
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// Whole days from today (local) until the given date: 0 = today, negative = already past.
export function daysUntilExamDate(value, now = new Date()) {
  const date = parseExamDate(value);
  if (!date) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((date - today) / (24 * 60 * 60 * 1000));
}

// 'closed' | 'closingSoon' (within 14 days) | 'open' | null (no deadline set)
export function registrationState(exam, now = new Date()) {
  const days = daysUntilExamDate(exam?.registration_deadline, now);
  if (days === null) return null;
  if (days < 0) return 'closed';
  if (days <= 14) return 'closingSoon';
  return 'open';
}

// An exam can now run on several dates (union_exam_sessions). Workers only ever need the one
// they can still sign up for, so this picks the soonest session that hasn't happened yet; with
// none left it keeps the most recent past one, so the card shows a real (if expired) date
// rather than going blank. Exams created before sessions existed, and not re-saved since, still
// carry their date on union_exams itself -- that's the last fallback.
export function nextExamSession(exam, now = new Date()) {
  const sessions = (exam?.sessions || []).filter(s => s?.test_date);
  if (sessions.length) {
    const byDate = [...sessions].sort((a, b) => String(a.test_date).localeCompare(String(b.test_date)));
    return byDate.find(s => daysUntilExamDate(s.test_date, now) >= 0) || byDate[byDate.length - 1];
  }
  if (exam?.next_test_date) {
    return { test_date: exam.next_test_date, registration_deadline: exam.registration_deadline || null };
  }
  return null;
}

// What to show for this exam: either "we'll schedule you individually", or the dates of the
// session that actually matters. Everything worker- and union-facing reads this, so the two
// modes can never disagree about which date is live.
export function examSchedule(exam, now = new Date()) {
  if (exam?.scheduled_individually) {
    return { individual: true, test_date: null, registration_deadline: null };
  }
  const session = nextExamSession(exam, now);
  return {
    individual: false,
    test_date: session?.test_date || null,
    registration_deadline: session?.registration_deadline || null,
  };
}

// True when the union filled in any part of a named contact for this exam.
export function examContact(exam) {
  const name  = (exam?.contact_name  || '').trim();
  const title = (exam?.contact_title || '').trim();
  const phone = (exam?.contact_phone || '').trim();
  const email = (exam?.contact_email || '').trim();
  if (!name && !title && !phone && !email) return null;
  return { name, title, phone, email };
}

// Soonest upcoming test first; exams with no date (TBA), individually scheduled, or already
// past sink to the bottom. Ranked on the live session, not the legacy column.
export function sortExamsByNextTest(exams, now = new Date()) {
  const rank = (e) => {
    const days = daysUntilExamDate(examSchedule(e, now).test_date, now);
    if (days === null) return [1, 0];
    if (days < 0) return [2, -days];
    return [0, days];
  };
  return [...exams].sort((a, b) => {
    const [ra, da] = rank(a);
    const [rb, db] = rank(b);
    return ra - rb || da - db || (a.name || '').localeCompare(b.name || '');
  });
}

// Study-guide links are typed by hand -- "ibew.org/guide" would otherwise resolve as a
// relative path on our own domain.
export function normalizeExternalUrl(raw) {
  const url = (raw || '').trim();
  if (!url) return '';
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
